import React, { useState } from "react";
import {
  capitalizeFirstLetter,
  formatDate,
} from "../../../utilities/functions";
import PlaceholderImage from "../../../assets/Ellipse 5.svg";
import { CircleLoader } from "react-spinners";
import SuspendUser from "./suspend";
import BlockUser from "./lock";
import MarkAsAssociate from "./mark-as-associate";
import UserTransactionDetails from "./user-transaction-details";
import useRequest from "../../../components/hooks/use-request";
import ImageViewer from "../../../components/viewImageModal";
import WalletBalance from "./wallet-balance";

interface DetailsProps {
  account: {
    id: string;
    balance: number;
    fullname: string;
    bankAccountNumber: number;
    email: string;
    bankName: string;
    status: string;
    created_at: string;
    orderId: string;
    password: string;
    phone: string;
    profilePhoto: string;
    socialMediaHandle: string;
    socialMediaPlatform: string;
    userType: string;
    isMarketer: boolean;
  };
  refetchAccount: () => void;
}

const Details: React.FC<DetailsProps> = ({ account, refetchAccount }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [lockKModalVisible, setLockModalVisible] = useState(false);
  const [associateModalVisible, setAssociateModalVisible] = useState(false);
  const { loading } = useRequest("/", "GET");
  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "#D1FFC9";
      case "blocked":
        return "#FCCFCF";
      case "inactive":
        return "#D9D9D9";
      case "suspended":
        return "#FBFCCF";
      default:
        return "transparent";
    }
  };

  const toggleImageModal = () => {
    setIsImageModalOpen(!isImageModalOpen);
  };

  const handleSuspendClick = () => {
    setModalVisible(true);
  };

  const handleLockClick = () => {
    setLockModalVisible(true);
  };

  const userType = account.userType;
  const Details = ({ title, value, img }: any) => {
    return (
      <div className="grid grid-cols-2 px-3 gap-14 mt-4">
        <p>{title}</p>
        <p className="text-left">{value}</p>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-2 justify-between gap-10 mt-10">
      <div className=" bg-white border border-[#fff]  pt-7 rounded-lg w-full">
        <div className="flex justify-between px-6">
          <p className="">Profile picture:</p>
          <img
            className="w-[117px] h-[117px] rounded-full cursor-pointer"
            src={account?.profilePhoto || PlaceholderImage}
            alt="profile"
            onClick={toggleImageModal}
          />
        </div>

        <Details
          title={userType === "buyer" ? "Name" : "Business Name"}
          value={
            <span className="inline-flex items-center gap-2 flex-wrap">
              {account?.fullname || "N/A"}
              {account?.isMarketer && (
                <span className="text-[10px] font-semibold text-[#0979A1] bg-[#E8F7FA] px-2 py-0.5 rounded-full">
                  Sales Associate
                </span>
              )}
            </span>
          }
        />

        <Details title="Phone number:" value={account?.phone || "N/A"} />

        <Details
          title="Email address:"
          value={
            account?.email ? (
              <a
                href={`mailto:${account.email}`}
                style={{ wordBreak: "break-all", whiteSpace: "normal" }}
                className="underline"
              >
                {account.email}
              </a>
            ) : (
              "N/A"
            )
          }
        />

        <Details
          title="Social media platform:"
          value={account?.socialMediaPlatform || "N/A"}
        />

        <Details
          title="Social media handle:"
          value={account?.socialMediaHandle || "N/A"}
        />

        <Details title="Bank name:" value={account?.bankName || "N/A"} />

        <Details
          title="Bank account number:"
          value={account?.bankAccountNumber || "N/A"}
        />

        <Details
          title="Date Joined:"
          value={formatDate(account?.created_at || "N/A")}
        />

        <div className="flex justify-between px-6 mt-4">
          <p className="">Account status:</p>
          <p
            className="text-[12px] w-20 p-2 text-center rounded-md"
            style={{
              backgroundColor: getStatusColor(account?.status || "N/A"),
            }}
          >
            {capitalizeFirstLetter(account?.status || "N/A")}
          </p>
        </div>

        <div className="flex h-36 items-center flex-col gap-6 mt-10 px-6">
          <button
            className={`border-2 ${
              account?.status === "blocked"
                ? "border-[#D9D9D9] text-[#D9D9D9]"
                : "border-[#0979A1]"
            } w-[389px] h-[43px] font-bold text-[#0979A1] rounded-md`}
            type="submit"
            onClick={handleSuspendClick}
            disabled={account?.status === "blocked"}
          >
            {loading ? (
              <CircleLoader color="#ffffff" loading={loading} size={20} />
            ) : account?.status === "suspended" ? (
              "Unsuspend account"
            ) : (
              "Suspend account"
            )}
          </button>

          {account?.isMarketer ? (
            <div className="w-[389px] h-[43px] flex items-center justify-center font-bold text-[#0979A1] bg-[#E8F7FA] rounded-md">
              Sales Associate
            </div>
          ) : (
            <button
              className="border-2 border-[#0979A1] w-[389px] h-[43px] font-bold text-[#0979A1] rounded-md"
              type="button"
              onClick={() => setAssociateModalVisible(true)}
            >
              Mark as associate
            </button>
          )}

          {/* <button
            className={`border-2 ${
              account?.status === "blocked"
                ? "bg-[#D9D9D9]"
                : "border-[#0979A1] bg-[#0979A1]"
            } w-[389px] h-[43px] font-bold text-[#fff] rounded-md`}
            type="submit"
            onClick={handleLockClick}
            disabled={account?.status === "blocked"}
          >
            {loading ? (
              <CircleLoader color="#ffffff" loading={loading} size={20} />
            ) : account?.status === "blocked" ? (
              "Blocked account"
            ) : (
              "Block account"
            )}
          </button> */}
        </div>

        <SuspendUser
          visible={modalVisible}
          handleClose={() => setModalVisible(false)}
          account={account}
          refetchAccount={refetchAccount}
        />

        <BlockUser
          visible={lockKModalVisible}
          handleClose={() => setLockModalVisible(false)}
          account={account}
          refetchAccount={refetchAccount}
        />

        <MarkAsAssociate
          visible={associateModalVisible}
          handleClose={() => setAssociateModalVisible(false)}
          account={account}
          refetchAccount={refetchAccount}
        />
      </div>
      <div className="flex flex-col gap-10">
        <WalletBalance account={account} refetchAccount={refetchAccount} />
        <UserTransactionDetails account={account} />
      </div>
      <ImageViewer
        visible={isImageModalOpen}
        handleClose={toggleImageModal}
        image={account?.profilePhoto || PlaceholderImage}
      />
    </div>
  );
};

export default Details;
